import { Event } from "event-system";
import { gsap } from "gsap";
import { SlowMo } from "gsap/EasePack";
import { Container, IDestroyOptions, Point, Sprite, Texture } from "pixi.js";
import { AnimationController } from "../../../../common/components/AnimationController";

export class DiceCollectAnimationView extends Container {
    private static readonly SPAWN_RADIUS: number = 0/*px*/;
    private static readonly MOVE_DURATION: number = 0.5/*s*/;
    private static readonly DICE_START_SCALE: number = 0.5/*unit*/;
    private static readonly DICE_END_SCALE: number = 0.15/*unit*/;
    private static readonly GLOW_START_SCALE: number = 0.1/*unit*/;
    private static readonly GLOW_END_SCALE: number = 0.05/*unit*/;
    private static readonly SHOWING_DELAY: number = 0.05/*s*/;
    private static readonly GLOW_PER_CUBE: number = 0.5;
    private static readonly DICE_FLY_AMPLITUDE_X: number = 200/*unit*/;

    private readonly animationController: AnimationController;
    private diceCollectAnimations: Array<gsap.core.Timeline>;
    private readonly diceTextures: Array<Texture>;
    private readonly glowTexture: Texture;
    private readonly collectPoint: Point;
    private itemsCount: number;
    public isActive: boolean;

    public get diceCollected(): Event {
        return this.animationController.animationDone;
    }

    constructor(textures: Array<Texture>, glowTexture: Texture, collectItemsCount: number, collectPoint: Point) {
        super();
        this.animationController = new AnimationController();
        this.diceTextures = textures;
        this.glowTexture = glowTexture;
        this.itemsCount = collectItemsCount;
        this.collectPoint = collectPoint;
        this.diceCollectAnimations = [];
    }

    //FIXME: Can be simplify
    public async startAnimation(): Promise<any> {
        for (let i = 0; i < this.itemsCount; i++) {
            const dice = new Sprite(this.diceTextures[Math.round(Math.random() * (this.diceTextures.length - 1))]);
            const spawnPoint = this.getRandomSpawnPoint();
            dice.position.copyFrom(spawnPoint);
            dice.anchor.set(0.5, 0.5);
            dice.rotation = Math.random() * Math.PI * 2;
            dice.visible = false;
            this.addChild(dice);
            this.animationController.addAnimation(this.setupCollectAnimation(dice, i * DiceCollectAnimationView.SHOWING_DELAY, DiceCollectAnimationView.DICE_START_SCALE, DiceCollectAnimationView.DICE_END_SCALE));
        }
        let glowCount = Math.floor(DiceCollectAnimationView.GLOW_PER_CUBE * this.itemsCount)
        let glowFlyAnimations: Array<Promise<void>> = [];
        while (glowCount-- > 0) {
            const glow = new Sprite(this.glowTexture);
            const spawnPoint = this.getRandomSpawnPoint();
            glow.position.copyFrom(spawnPoint);
            glow.anchor.set(0.5, 0.5);
            glow.visible = false;
            this.addChild(glow);
            glowFlyAnimations.push(this.setupCollectAnimation(glow, glowCount * DiceCollectAnimationView.SHOWING_DELAY, DiceCollectAnimationView.GLOW_START_SCALE, DiceCollectAnimationView.GLOW_END_SCALE));
        }
        this.isActive = true;
        return await Promise.all([this.animationController.startAnimation(), glowFlyAnimations]);
    }

    public setAnimationProgress(progress: number): void {
        this.diceCollectAnimations?.forEach(timeLine => timeLine?.progress(progress));
    }

    private getRandomSpawnPoint(): Point {
        const theta = Math.random() * Math.PI * 2;
        return new Point(DiceCollectAnimationView.SPAWN_RADIUS * Math.cos(theta) + 100, DiceCollectAnimationView.SPAWN_RADIUS * Math.sin(theta) - 200);
    }

    private async setupCollectAnimation(sprite: Sprite, showDelay: number, startScale: number, endScale: number): Promise<any> {
        const startPosition = sprite.position.clone();
        const timeline = gsap.timeline();
        this.diceCollectAnimations.push(timeline);
        timeline.delay(showDelay);
        timeline.set(sprite, { visible: true });
        timeline.fromTo(sprite,
            {
                y: startPosition.y,
            },
            {
                y: this.collectPoint.y,
                duration: DiceCollectAnimationView.MOVE_DURATION,
                ease: "power1.out"
            });
        timeline.fromTo(sprite,
            {
                x: startPosition.x,
            },
            {
                x: startPosition.x - DiceCollectAnimationView.DICE_FLY_AMPLITUDE_X,
                duration: DiceCollectAnimationView.MOVE_DURATION,
                ease: SlowMo.ease.config(0.05, 0.1, true)
            }, "<");
        timeline.to(sprite,
            {
                x: this.collectPoint.x,
                duration: DiceCollectAnimationView.MOVE_DURATION * 0.5,
                ease: "none"
            }, "<50%");
        timeline.fromTo(sprite.scale,
            {
                x: startScale,
                y: startScale
            },
            {
                x: endScale,
                y: endScale,
                duration: DiceCollectAnimationView.MOVE_DURATION * 0.5,
                ease: "none"
            }, "<");
        return timeline.then(() => {
            timeline.kill();
            sprite.destroy()
        });
    }

    public updateCollectItemsCount(value: number): void {
        this.itemsCount = value;
    }

    override destroy(options?: boolean | IDestroyOptions): void {
        this.diceCollectAnimations.forEach(timeLine => timeLine?.kill());
        super.destroy(options);
    }
}
